import type React from "react";
import { useState, useEffect, useMemo } from "react";
import { Autocomplete, CircularProgress, Chip } from "@mui/material";
import { IService } from "../../../../types/package";
import apiService from "../../../../service/Api";
import useDebounce from "../../../../hooks/useDebounce";
import CustomTextField from "../../../../components/common/Textfield";

interface ServiceAutocompleteProps {
  value: IService[];
  onChange: (services: IService[]) => void;
  label?: string;
  placeholder?: string;
}

const ServiceAutocomplete: React.FC<ServiceAutocompleteProps> = ({
  value,
  onChange,
  label = "Base Services",
  placeholder = "Search services (type at least 3 characters)...",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<IService[]>([]);
  const [loading, setLoading] = useState(false);

  // ✅ Use your custom hook to debounce input value
  const debouncedInput = useDebounce(inputValue, 500);

  // ✅ Fetch services only after debounce delay
  useEffect(() => {
    const fetchServices = async () => {
      if (debouncedInput.length < 3) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await apiService.get<{
          count: number;
          results: IService[];
        }>(
          `/api/packages/package-service/?search=${encodeURIComponent(
            debouncedInput
          )}`
        );

        const results = response.data.results || [];
        const selectedIds = value.map((s) => s.id);
        const filteredResults = results.filter(
          (service) => !selectedIds.includes(service.id)
        );
        setOptions(filteredResults);
      } catch (error) {
        console.error("Error searching services:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [debouncedInput, value]); // Runs only when debounced input changes

  // Handle typing in the input box
  const handleInputChange = (
    _event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    setInputValue(newInputValue);
  };

  // Handle selection of items
  const handleChange = (_event: React.SyntheticEvent, newValue: IService[]) => {
    onChange(newValue);
    setInputValue(""); // Clear input after selection
  };

  const memoizedOptions = useMemo(() => options, [options]);

  return (
    <Autocomplete
      multiple
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={memoizedOptions}
      getOptionLabel={(option) => option.name}
      loading={loading}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <CustomTextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          helperText="Select services to add to this package"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            label={option.name}
            {...getTagProps({ index })}
            key={option.id}
          />
        ))
      }
      noOptionsText={
        inputValue.length < 3
          ? "Type at least 3 characters to search"
          : "No services found"
      }
    />
  );
};

export default ServiceAutocomplete;
